import { Banner } from 'component/banner';
import { colorSurfaceBannerInfo } from 'component/color';
import type { DropdownFieldOption } from 'component/form';
import { DropdownField, FormField } from 'component/form';
import { SkeletonText } from 'component/skeleton';
import { UtilityText } from 'component/text';
import type {
    InstanceManagerConfig,
    NewInstance,
    NewInstancePostCreationTriggerProps,
} from 'feature/editorial-dash/question-topic-manager';
import type { SpacesConfig } from 'feature/editorial-dash/spaces';
import {
    ChannelAssignmentDropDown,
    ChannelSelectionDropDown,
    useMyChannels,
    useMyCurrentChannel,
} from 'open-modules/zdf/component/channels/channel-dropdown';
import { EditorialChannel, moduleMapChannel, moduleMapInstanceToChannel } from 'open-modules/zdf/component/channels/channeltypes';
import { useEffect, useState } from 'react';
import { View } from 'react-native';
import type { QuestionConfig } from 'structure/question';
import type { Datastore } from 'util/datastore';
import { useDatastore, useModuleMap, useModuleObject } from 'util/datastore';
import { type Feature, t } from 'util/stdtypes';

export const ChannelSelectionFeature: Feature<InstanceManagerConfig & SpacesConfig & QuestionConfig, {}> = {
    name: 'Channel Selection/Reassignment',
    key: 'editorialdashchannelselection',
    dev: true,
    providesConfig: {
        newInstanceWidgets: [NewInstanceChannelWidget],
        instanceBottomLeftWidgets: [ChannelAssignmentDropDown],
        newInstancePostCreateTriggers: [assignChannelAfterInstanceCreation],
        newScreenWidgets: [
            {
                slot: 'after-name',
                component: NewSpaceScreenWidget,
                onCreate: async (datastore: Datastore, questionKey: string, values: Record<string, any>) => {
                    if (values.channelKey) {
                        await datastore.callServerAsync('channel', 'assignContentToChannel', {
                            instanceKey: questionKey,
                            channelKey: values.channelKey,
                            structureKey: 'question',
                        });
                    }
                },
            },
        ],
        editSidebarSections: [
            {
                slot: 'after-thread-header',
                headingLabel: t('Channel'),
                component: EditQuestionSidebarSection,
            },
        ],
    },
};
// Note: Used to only show this for structures that need channel assignments
const allowedStructures = ['question', 'topic'];

export type NewInstanceWithChannel = NewInstance & {
    channelKey: string;
};

async function assignChannelAfterInstanceCreation(props: NewInstancePostCreationTriggerProps): Promise<void> {
    if (!allowedStructures.includes(props.structureKey)) {
        return;
    }
    const newInstanceWithChannel = props.instance as NewInstanceWithChannel;
    await props.datastore.callServerAsync('channel', 'assignContentToChannel', {
        instanceKey: props.instanceKey,
        channelKey: newInstanceWithChannel.channelKey,
        structureKey: props.structureKey,
    });
}

export type NewInstanceChannelWidgetProps = {
    structureKey: string;
    instance: any;
    setInstance: any;
};

export function NewInstanceChannelWidget({ structureKey, instance, setInstance }: NewInstanceChannelWidgetProps) {
    const currentChannel = useMyCurrentChannel();

    useEffect(() => {
        if (currentChannel != null) {
            setInstance((instance) => ({ ...instance, channelKey: currentChannel.key }));
        }
    }, [currentChannel]);

    if (!allowedStructures.includes(structureKey)) {
        return;
    }

    return (
        <View>
            <ChannelSelectionDropDown />
        </View>
    );
}

type NewSpaceScreenWidgetProps = {
    values: Record<string, any>;
    setValues: (values: Record<string, any>) => void;
};

export function NewSpaceScreenWidget({ values, setValues }: NewSpaceScreenWidgetProps) {
    return (
        <ChannelFormField
            onChange={(value) => {
                setValues({ ...values, channelKey: value });
            }}
            value={values.channelKey}
        />
    );
}

export function EditQuestionSidebarSection() {
    const datastore = useDatastore();
    const concatenatedKey = datastore.getStructureKey() + '/' + datastore.getInstanceKey();
    const currentChannels = useModuleObject(moduleMapInstanceToChannel, concatenatedKey);
    const currentChannelKey = Object.keys(currentChannels || {})[0];

    const [selectedChannelKey, setSelectedChannelKey] = useState(currentChannelKey);

    useEffect(() => {
        setSelectedChannelKey(currentChannelKey);
    }, [currentChannelKey]);

    return (
        <ChannelFormField
            onChange={(value) => {
                if (currentChannelKey) {
                    datastore.callServerAsync('channel', 'reassignContentToChannel', {
                        newChannelKey: value,
                        oldChannelKey: currentChannelKey,
                        instanceKey: datastore.getInstanceKey(),
                        structureKey: datastore.getStructureKey(),
                    });
                } else {
                    datastore.callServerAsync('channel', 'assignContentToChannel', {
                        instanceKey: datastore.getInstanceKey(),
                        channelKey: value,
                        structureKey: 'question',
                    });
                }
                setSelectedChannelKey(value ?? '');
            }}
            value={selectedChannelKey}
        />
    );
}

type ChannelFormFieldProps = {
    onChange: (channelKey: string | null) => void;
    value: string | null;
};

export function ChannelFormField({ onChange, value }: ChannelFormFieldProps) {
    const userChannels = useMyChannels();
    const userChannelArray = Object.values(userChannels || {});
    const allChannels = useModuleMap(moduleMapChannel);

    const options: DropdownFieldOption[] = userChannelArray.map((channel) => {
        return {
            text: channel.name,
            key: channel.key,
        };
    });

    if (null === userChannels) {
        return (
            <FormField label={t('Channel')}>
                <SkeletonText height={48} width={'100%'} />
            </FormField>
        );
    }

    // If there's a channel assigned but the user doesn't have access to it,
    // display it anyway but disable the dropdown.
    let disabled = false;
    if (userChannelArray.length === 0 && value && allChannels && allChannels[value]) {
        disabled = true;
        const channel = allChannels[value];
        options.unshift({
            text: channel.name,
            key: channel.key,
        });
    }

    if (options.length === 0) {
        return (
            <FormField label={t('Channel')}>
                <Banner color={colorSurfaceBannerInfo}>
                    <UtilityText label={t('No channels available')} />
                </Banner>
            </FormField>
        );
    }

    if (value) {
        options.unshift({
            label: t('No channel'),
            key: 'no-channel',
        });
    }
    return (
        <FormField label={t('Channel')}>
            <DropdownField
                disabled={disabled}
                onChange={(value) => onChange(value === 'no-channel' ? null : value)}
                options={options}
                placeholder={t('Select a channel')}
                value={value}
            />
        </FormField>
    );
}
